import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"
import { DatePicker } from "@/components/ui/date-picker"
import { TimePicker } from "@/components/ui/time-picker"
import { Trash2 } from "lucide-react"
import type { Task } from "@crm-src/types/task"

interface EditTaskState {
  name: string
  description: string
  priority: Task["priority"]
  due_date: string
  due_time: string
  assigned_to_id: number | undefined
  status: Task["status"]
  is_recurring: boolean
  recurrence_pattern: "daily" | "weekly" | "monthly" | "yearly" | "custom"
  recurrence_interval: number
  recurrence_end_date: string
  recurrence_count: number | undefined
}

interface EditTaskDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  taskToEdit: Task | null
  editTask: EditTaskState
  setEditTask: (task: EditTaskState) => void
  users: any[]
  userIsAdmin: boolean
  onSubmit: () => void
  onDelete: () => void
}

export function EditTaskDialog({
  open,
  onOpenChange,
  taskToEdit,
  editTask,
  setEditTask,
  users,
  userIsAdmin,
  onSubmit,
  onDelete,
}: EditTaskDialogProps) {
  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="md" fullWidth>
      <DialogTitle>Edit Task</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Update task details and recurrence settings
        </DialogContentText>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 2 }}>
          <TextField
            id="edit_name"
            label="Task Name *"
            size="small"
            fullWidth
            value={editTask.name}
            onChange={(e) => setEditTask({...editTask, name: e.target.value})}
            placeholder="Enter task name"
          />

          <TextField
            id="edit_description"
            label="Description"
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={editTask.description}
            onChange={(e) => setEditTask({...editTask, description: e.target.value})}
            placeholder="Enter task description"
          />

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              id="edit_status"
              label="Status"
              select
              size="small"
              fullWidth
              value={editTask.status}
              onChange={(e) => setEditTask({...editTask, status: e.target.value as Task["status"]})}
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </TextField>

            <TextField
              id="edit_priority"
              label="Priority"
              select
              size="small"
              fullWidth
              value={editTask.priority}
              onChange={(e) => setEditTask({...editTask, priority: e.target.value as Task["priority"]})}
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="urgent">Urgent</MenuItem>
            </TextField>
          </Box>

          {userIsAdmin && (
            <TextField
              id="edit_assigned_to"
              label="Assign To"
              select
              size="small"
              fullWidth
              value={editTask.assigned_to_id?.toString() || "unassigned"}
              onChange={(e) => setEditTask({
                ...editTask,
                assigned_to_id: e.target.value === "unassigned" ? undefined : parseInt(e.target.value)
              })}
            >
              <MenuItem value="unassigned">Unassigned</MenuItem>
              {users.map((user: any) => (
                <MenuItem key={user.id} value={user.id.toString()}>
                  {user.full_name}
                </MenuItem>
              ))}
            </TextField>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel>Due Date *</FormLabel>
              <DatePicker
                value={editTask.due_date}
                onChange={(date) => setEditTask({...editTask, due_date: date})}
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <FormLabel>Due Time *</FormLabel>
              <TimePicker
                value={editTask.due_time}
                onChange={(time) => setEditTask({...editTask, due_time: time})}
              />
            </Box>
          </Box>

          {/* Recurring Task Options */}
          {!taskToEdit?.is_recurring_instance && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, borderTop: 1, borderColor: "divider", pt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    id="edit_is_recurring"
                    checked={editTask.is_recurring}
                    onChange={(e) => setEditTask({...editTask, is_recurring: e.target.checked})}
                  />
                }
                label={<Typography sx={{ fontWeight: 600 }}>Make this a recurring task</Typography>}
              />

              {editTask.is_recurring && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, ml: 3 }}>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <TextField
                      id="edit_recurrence_pattern"
                      label="Repeat Pattern"
                      select
                      size="small"
                      fullWidth
                      value={editTask.recurrence_pattern}
                      onChange={(e) => setEditTask({...editTask, recurrence_pattern: e.target.value as EditTaskState["recurrence_pattern"]})}
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                      <MenuItem value="yearly">Yearly</MenuItem>
                      <MenuItem value="custom">Custom (days)</MenuItem>
                    </TextField>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <TextField
                        id="edit_recurrence_interval"
                        label="Every"
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{ min: 1 }}
                        value={editTask.recurrence_interval}
                        onChange={(e) => setEditTask({...editTask, recurrence_interval: parseInt(e.target.value) || 1})}
                      />
                      <Typography variant="caption" sx={{ color: "text.disabled" }}>
                        {editTask.recurrence_pattern === 'custom' ? 'days' : editTask.recurrence_pattern.replace('ly', '(s)')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    <FormLabel>End Date (optional)</FormLabel>
                    <DatePicker
                      value={editTask.recurrence_end_date}
                      onChange={(date) => setEditTask({...editTask, recurrence_end_date: date})}
                      placeholder="No end date"
                    />
                    <Typography variant="caption" sx={{ color: "text.disabled" }}>Leave empty for no end date</Typography>
                  </Box>

                  <TextField
                    id="edit_recurrence_count"
                    label="Or stop after (occurrences)"
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 1 }}
                    value={editTask.recurrence_count || ""}
                    onChange={(e) => setEditTask({
                      ...editTask,
                      recurrence_count: e.target.value ? parseInt(e.target.value) : undefined
                    })}
                    placeholder="Leave empty for no limit"
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", alignItems: "center" }}>
        <Button
          variant="contained"
          color="error"
          onClick={onDelete}
          startIcon={<Trash2 size={16} />}
        >
          Delete Task
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={!editTask.name || !editTask.due_date || !editTask.due_time}
          >
            Update Task
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
